import { useCallback, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

type Result = ModelHookResult<
	{
		isLiked: boolean;
		likeCount: number;
		likeCountLocalized: string;
	},
	{
		updateLike: (like: boolean) => void;
	}
>;

export function useLikePetitionUpdateState({
	petitionUpdate: { likes, likesLocalized, viewerLiked },
}: {
	petitionUpdate: { likes: number | null; likesLocalized: string | null; viewerLiked: boolean };
}): Result {
	const { localizeNumber } = useI18n();

	const [isLiked, setIsLiked] = useState(viewerLiked);
	const [likeCount, setLikeCount] = useState(likes ?? 0);
	// Seed the displayed count from the server-localized string. Calling localizeNumber during the
	// server render causes hydration mismatches (see docs/front-end/ssr.md — localize on the server),
	// so we never call it at render: the count only shows when > 0 (where likesLocalized is present),
	// and localizeNumber runs only in updateLike, which fires solely on client interaction.
	const [likeCountLocalized, setLikeCountLocalized] = useState(likesLocalized ?? '');

	const updateLike = useCallback(
		(like: boolean) => {
			setIsLiked(like);
			// Read likeCount from the closure (not a functional update): on failure, toggleLike calls
			// updateLike again with the original liked state and the stale closure restores the count.
			// Clamp at 0: an already-liked update can start from a 0/unknown count, so unliking must not go negative.
			const nextCount = like === isLiked ? likeCount : Math.max(0, likeCount + (like ? 1 : -1));
			setLikeCount(nextCount);
			setLikeCountLocalized(localizeNumber(nextCount));
		},
		[isLiked, likeCount, localizeNumber],
	);

	return {
		data: {
			isLiked,
			likeCount,
			likeCountLocalized,
		},
		actions: {
			updateLike,
		},
	};
}
